import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FetchResult, useApolloClient } from '@apollo/client';
import LogRocket from 'logrocket';

import { graphqlSchema } from '../services/graphql.schema';
import GraphQlClient from '../services/graphql';
import { setFiscalpopApiAuthToken } from '../services/fiscalpopApi';
import { AmazonProfile, FiscalpopProfile, ProfileQL } from '../models/Profile';

const AUTH_TOKEN = 'mws_profileid';
const AUTH_FAIL_REDIRECT_URL = process.env.REACT_APP_HOME_REDIRECT_URL!;

interface CurrentUserContextValue {
    currentUser: ProfileQL | null;
    userFetched: boolean;
    modifyAmazonProfile: (props: Partial<AmazonProfile>) => void;
    recallCurrentUser: () => Promise<void>;
    submitFiscalpopClient: (client: FiscalpopProfile) => Promise<FetchResult>;
    logOut: () => void;
}

const CurrentUserContext = createContext<CurrentUserContextValue | undefined>(undefined);

function redirectToAuthFail() {
    localStorage.removeItem(AUTH_TOKEN);
    setFiscalpopApiAuthToken(null);
    window.location.replace(AUTH_FAIL_REDIRECT_URL);
}

export function CurrentUserProvider({ children }: { children: React.ReactNode }) {
    const apolloClient = useApolloClient();
    const [currentUser, setCurrentUser] = useState<ProfileQL | null>(null);
    const [userFetched, setUserFetched] = useState(false);
    const identifiedRef = useRef(false);

    const applyUser = useCallback((user: ProfileQL) => {
        setCurrentUser(user);
        setFiscalpopApiAuthToken(user?.amazonProfile?.authToken ?? null);
        if (!identifiedRef.current && user?.amazonProfile) {
            identifiedRef.current = true;
            LogRocket.identify(`${user.amazonProfile.mwsSelledId}`, {
                name: `${user.amazonProfile.companyName}`,
                email: user.amazonProfile.email,
                linker: user.amazonProfile.linkerId,
                subscriptionType: 'default',
                canBill: !!user.fiscalpopProfile && !!user.fiscalpopProfile.rfc ? user.fiscalpopProfile.rfc : '',
                hasDonePayments: !!user.billing && !!user.billing.lastPaymentId ? user.billing.lastPaymentId : '',
            });
        }
    }, []);

    const fetchMe = useCallback(async () => {
        try {
            const response = await apolloClient.query<{ me: ProfileQL }>({
                query: graphqlSchema.PROFILE.me,
                fetchPolicy: 'network-only',
            });
            const me = response.data?.me;
            if (!me || !me.amazonProfile) {
                redirectToAuthFail();
                return;
            }
            // Amazon SP-API permission lapsed — bounce to consent before showing the app.
            if (me.amazonProfile.spAccessLastGrantFail) {
                if (me.amazonProfile.spConsentUrl) {
                    window.location.replace(me.amazonProfile.spConsentUrl);
                    return;
                }
                redirectToAuthFail();
                return;
            }
            applyUser(me);
            setUserFetched(true);
        } catch (e) {
            console.error('[CurrentUserContext] Error fetching user: ', e);
            redirectToAuthFail();
        }
    }, [apolloClient, applyUser]);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const modifyAmazonProfile = useCallback((props: Partial<AmazonProfile>) => {
        // Apollo cache results are frozen; rebuild the wrapping profile object instead of mutating it.
        setCurrentUser(curr => curr ? ({
            ...curr,
            amazonProfile: { ...curr.amazonProfile, ...props },
        }) : curr);
    }, []);

    const recallCurrentUser = useCallback(() => fetchMe(), [fetchMe]);

    const submitFiscalpopClient = useCallback(async (client: FiscalpopProfile) => {
        const result = await GraphQlClient.submitFiscalpopClient(client);
        await fetchMe();
        return result;
    }, [fetchMe]);

    const logOut = useCallback(() => {
        setFiscalpopApiAuthToken(null);
        GraphQlClient.logOut();
    }, []);

    const value: CurrentUserContextValue = {
        currentUser,
        userFetched,
        modifyAmazonProfile,
        recallCurrentUser,
        submitFiscalpopClient,
        logOut,
    };

    return (
        <CurrentUserContext.Provider value={value}>
            {children}
        </CurrentUserContext.Provider>
    );
}

export function useCurrentUser(): CurrentUserContextValue {
    const ctx = useContext(CurrentUserContext);
    if (!ctx) {
        throw new Error('useCurrentUser must be used within a CurrentUserProvider');
    }
    return ctx;
}
